<div *ngIf="!isHidden" class="unified-header w-full bg-base-100 text-base-content border-b border-base-200 shadow-sm sticky top-0 z-50">
  <nav class="h-14 px-4 lg:px-6 flex items-center justify-between gap-4">

    <!-- ── LEFT: Logo ── -->
    <a routerLink="/" class="flex items-center gap-2 shrink-0">
      @if (tenantLogoUrl) {
        <img [src]="tenantLogoUrl" [alt]="currentTenant?.name || 'Academy Logo'" class="h-8 max-w-[200px] object-contain" />
      } @else if (isDefaultTenant) {
        <img src="assets/images/logo-single.png" alt="Learnbird Logo" class="h-7" />
        <img src="assets/images/logo-text.png" alt="Learnbird Logo" class="h-5 hidden sm:block" />
      } @else {
        <span class="font-bold text-lg text-base-content tracking-tight truncate max-w-[200px]">{{ currentTenant?.name }}</span>
      }
    </a>

    <!-- ── CENTER: Primary nav (desktop, hidden on dashboard — sidebar handles it) ── -->
    <div class="hidden lg:flex items-center gap-1 flex-1 ml-6" [class.invisible]="isDashboardRoute">
      @if (!isAppMode) {
        <a routerLink="/live-classes" routerLinkActive="nav-link-active"
          class="nav-link px-3 py-2 rounded-md text-sm font-medium text-base-content hover:text-primary hover:bg-base-200 transition-colors duration-150 relative">
          Live Classes<span class="nav-underline"></span>
        </a>
        @if (showCourses) {
          <a routerLink="/courses" routerLinkActive="nav-link-active"
            class="nav-link px-3 py-2 rounded-md text-sm font-medium text-base-content hover:text-primary hover:bg-base-200 transition-colors duration-150 relative">
            Courses<span class="nav-underline"></span>
          </a>
        }
        @if (isDefaultTenant) {
          <a routerLink="/tutors" routerLinkActive="nav-link-active"
            class="nav-link px-3 py-2 rounded-md text-sm font-medium text-base-content hover:text-primary hover:bg-base-200 transition-colors duration-150 relative">
            Find a Tutor<span class="nav-underline"></span>
          </a>
          <a routerLink="/teach" routerLinkActive="nav-link-active"
            class="nav-link px-3 py-2 rounded-md text-sm font-medium text-base-content hover:text-primary hover:bg-base-200 transition-colors duration-150 relative">
            Become a Tutor<span class="nav-underline"></span>
          </a>
        }
      }
    </div>

    <!-- ── RIGHT: Auth + Dashboard CTA (desktop) ── -->
    <div class="hidden lg:flex items-center gap-3 shrink-0 ml-auto">
      @if (isThemeToggleAvailable) {
        <button
          type="button"
          (click)="toggleTheme()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-circle text-base-content hover:bg-base-200"
          [attr.aria-label]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
          [title]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          @if (isDarkMode) {
            <i class="fa-solid fa-sun text-warning text-base"></i>
          } @else {
            <i class="fa-solid fa-moon text-base-content/70 text-base"></i>
          }
        </button>
      }
      @if (isLoggedIn && !isDashboardRoute) {
        <a routerLink="/dashboard" class="daisy-btn daisy-btn-sm daisy-btn-primary">
          <i class="fa-solid fa-gauge"></i>
          My Dashboard
        </a>
      }
      <black-auth-control></black-auth-control>
    </div>

    <!-- ── MOBILE RIGHT: Avatar + Hamburger ── -->
    <div class="lg:hidden flex items-center gap-1.5 ml-auto">
      @if (isThemeToggleAvailable) {
        <button
          type="button"
          (click)="toggleTheme()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-circle text-base-content hover:bg-base-200"
          [attr.aria-label]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
          [title]="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          @if (isDarkMode) {
            <i class="fa-solid fa-sun text-warning text-sm"></i>
          } @else {
            <i class="fa-solid fa-moon text-base-content/70 text-sm"></i>
          }
        </button>
      }
      @if (isLoggedIn && !isDashboardRoute) {
        <button type="button" (click)="openMobileNav()"
          class="flex items-center justify-center hover:bg-base-200 rounded-full p-1 transition-colors"
          aria-label="Open menu">
          <div class="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center overflow-hidden text-sm font-semibold">
            <ng-container *ngIf="getAvatarUrl(loggedInUser?.profile?.personal?.avatar); else mobileInitial">
              <img [src]="getAvatarUrl(loggedInUser?.profile?.personal?.avatar)" alt="Avatar" class="w-full h-full object-cover" />
            </ng-container>
            <ng-template #mobileInitial>{{ avatarInitial }}</ng-template>
          </div>
        </button>
      } @else if (!isLoggedIn) {
        <a routerLink="/auth/login" class="daisy-btn daisy-btn-primary daisy-btn-sm text-xs px-3">
          <i class="fa-solid fa-user-tie"></i> Login
        </a>
      }
      <button type="button" (click)="handleMobileHamburger()"
        class="p-2 hover:bg-base-200 rounded-md transition-colors text-base-content text-lg flex items-center justify-center"
        [attr.aria-label]="isDashboardRoute ? 'Toggle sidebar' : 'Open menu'">
        <i class="fa-solid fa-bars"></i>
      </button>
    </div>
  </nav>
</div>

<!-- ── MOBILE NAV PANEL — public routes only, only in DOM when open ── -->
@if (!isHidden && !isDashboardRoute && showMobileNav) {
  <div class="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/50" (click)="closeMobileNav()"></div>

    <!-- Slide-in panel -->
    <div class="absolute right-0 top-0 h-full w-80 bg-base-100 text-base-content shadow-xl flex flex-col">

      <!-- Panel header — h-14 matches the sticky header -->
      <div class="h-14 flex items-center justify-between px-4 border-b border-base-200 shrink-0">
        <a routerLink="/" class="flex items-center gap-2" (click)="closeMobileNav()">
          @if (tenantLogoUrl) {
            <img [src]="tenantLogoUrl" [alt]="currentTenant?.name || 'Academy Logo'" class="h-8 max-w-[180px] object-contain" />
          } @else if (isDefaultTenant) {
            <img src="assets/images/logo-single.png" alt="Learnbird Logo" class="h-7" />
            <img src="assets/images/logo-text.png" alt="Learnbird Logo" class="h-5" />
          } @else {
            <span class="font-bold text-lg text-base-content tracking-tight truncate max-w-[180px]">{{ currentTenant?.name }}</span>
          }
        </a>
        <button type="button" (click)="closeMobileNav()"
          class="p-2 hover:bg-base-200 rounded-md transition-colors" aria-label="Close menu">
          <i class="fa-solid fa-xmark text-base-content/80 text-xl"></i>
        </button>
      </div>

      <!-- Scrollable body -->
      <div class="flex-1 overflow-y-auto flex flex-col">

        <!-- User card (logged in) -->
        @if (isLoggedIn) {
          <div class="flex items-center gap-3 px-4 py-3 border-b border-base-200 bg-base-200/50">
            <div class="w-10 h-10 rounded-full bg-primary text-primary-content flex items-center justify-center overflow-hidden shrink-0 font-semibold">
              <ng-container *ngIf="getAvatarUrl(loggedInUser?.profile?.personal?.avatar); else panelInitial">
                <img [src]="getAvatarUrl(loggedInUser?.profile?.personal?.avatar)" alt="Avatar" class="w-full h-full object-cover" />
              </ng-container>
              <ng-template #panelInitial>{{ avatarInitial }}</ng-template>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-base-content truncate">{{ fullName || loggedInUser?.username }}</p>
              <p class="text-xs text-base-content/70 truncate">{{ loggedInUser?.username }}</p>
            </div>
          </div>
        }

        <div class="p-4 flex flex-col gap-5">

          <!-- Guest welcome card -->
          @if (!isLoggedIn) {
            <div class="p-4 bg-base-200 rounded-xl border border-base-300">
              <p class="text-sm font-semibold text-base-content mb-1">Welcome to {{ !isDefaultTenant ? (currentTenant?.name || 'Academy') : 'Learnbird' }}</p>
              <p class="text-xs text-base-content/70 mb-3">{{ !isDefaultTenant ? 'Sign in to access your classes and courses.' : 'Explore classes or find a tutor that fits your goals.' }}</p>
              <div class="flex gap-2">
                <a routerLink="/auth/login" (click)="closeMobileNav()"
                  class="daisy-btn daisy-btn-outline daisy-btn-primary daisy-btn-sm flex-1 text-xs">Login</a>
                <a routerLink="/auth/register" (click)="closeMobileNav()"
                  class="daisy-btn daisy-btn-primary daisy-btn-sm flex-1 text-xs">Sign Up</a>
              </div>
            </div>
          }

          <!-- Explore -->
          @if (!isAppMode) {
            <div>
              <p class="text-[11px] font-semibold text-base-content/50 uppercase tracking-widest px-1 mb-1.5">Explore</p>
              <div class="flex flex-col gap-0.5">
                <a routerLink="/live-classes" (click)="closeMobileNav()"
                  class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                  <i class="fa-solid fa-tower-broadcast text-primary w-4 text-center"></i>
                  <span>Live Classes</span>
                </a>
                @if (showCourses) {
                  <a routerLink="/courses" (click)="closeMobileNav()"
                    class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                    <i class="fa-solid fa-graduation-cap text-primary w-4 text-center"></i>
                    <span>Courses</span>
                  </a>
                }
                @if (isDefaultTenant) {
                  <a routerLink="/tutors" (click)="closeMobileNav()"
                    class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                    <i class="fa-solid fa-chalkboard-user opacity-70 w-4 text-center"></i>
                    <span>Find a Tutor</span>
                  </a>
                  <a routerLink="/teach" (click)="closeMobileNav()"
                    class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                    <i class="fa-solid fa-graduation-cap opacity-70 w-4 text-center"></i>
                    <span>Become a Tutor</span>
                  </a>
                  <a routerLink="/contact" (click)="closeMobileNav()"
                    class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                    <i class="fa-solid fa-envelope opacity-70 w-4 text-center"></i>
                    <span>Contact Us</span>
                  </a>
                }
              </div>
            </div>
          }

          <!-- My Account (logged in only) -->
          @if (isLoggedIn) {
            <div>
              <p class="text-[11px] font-semibold text-base-content/50 uppercase tracking-widest px-1 mb-1.5">My Account</p>
              <div class="flex flex-col gap-0.5">
                <a routerLink="/dashboard" (click)="closeMobileNav()"
                  class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                  <i class="fa-solid fa-gauge opacity-70 w-4 text-center"></i>
                  <span>Dashboard</span>
                </a>
                <a routerLink="/dashboard/profile" (click)="closeMobileNav()"
                  class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                  <i class="fa-solid fa-user opacity-70 w-4 text-center"></i>
                  <span>My Profile</span>
                </a>
                @if (isUserInstructor && showTutorProfile) {
                  <a routerLink="/dashboard/profile/tutor-profile" (click)="closeMobileNav()"
                    class="flex items-center gap-3 px-3 py-2.5 text-base-content hover:text-primary hover:bg-base-200 rounded-lg transition-colors text-sm font-medium">
                    <div class="relative w-4 flex justify-center shrink-0">
                      <i class="fa-solid fa-id-card opacity-70"></i>
                      @if (hasIncompleteTutorProfile) {
                        <span class="absolute -top-1 -right-1 flex h-2 w-2">
                          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                        </span>
                      }
                    </div>
                    <span>Tutor Profile</span>
                  </a>
                }
              </div>
            </div>
          }

        @if (isThemeToggleAvailable) {
          <div class="pt-2 border-t border-base-200 flex items-center justify-between">
            <span class="text-xs font-semibold text-base-content/60">Appearance</span>
            <button
              type="button"
              (click)="toggleTheme()"
              class="daisy-btn daisy-btn-xs daisy-btn-outline gap-1.5 border-base-300 text-base-content"
            >
              @if (isDarkMode) {
                <i class="fa-solid fa-sun text-warning"></i> Light Mode
              } @else {
                <i class="fa-solid fa-moon text-base-content/70"></i> Dark Mode
              }
            </button>
          </div>
        }

      </div>
    </div>

      <!-- Footer: sign out -->
      @if (isLoggedIn) {
        <div class="px-4 py-3 border-t border-base-200 shrink-0">
          <button type="button" (click)="logout()"
            class="flex items-center gap-3 px-3 py-2.5 text-error hover:bg-error/10 rounded-lg transition-colors w-full text-left text-sm font-medium">
            <i class="fa-solid fa-arrow-right-from-bracket w-4 text-center"></i>
            <span>Sign Out</span>
          </button>
        </div>
      }
    </div>
  </div>
}
